// src/components/Form/LeaveApply.jsx - Complete and Updated
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FaArrowLeft, FaCalendarAlt, FaUserTie, FaCheckCircle, FaExclamationTriangle,
  FaTimes, FaSearch, FaEdit, FaTrash, FaInfoCircle, FaCog, FaBuilding
} from 'react-icons/fa';
import { UserContext } from '../../Context/UserContext';
import CustomerCustomizationModal from "./CustomerCustomizationModal";
import { checkIsAdmin, checkIsGlobalAdmin } from "../../utils/authUtils";
import useCustomization from '../../hooks/useCustomization';
import './CreateCustomerPage.css';

const LeaveApply = () => {
  const navigate = useNavigate();
  const { visibleFields, isFieldVisible, handleCustomizationRefresh } = useCustomization('Leave Apply');
  const { user, baseUrl, configLoading, getHeaders } = useContext(UserContext);
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [form, setForm] = useState({
    employee_id: '',
    leave_type_id: '',
    leave_mode: 'FULL_DAY',
    from_date: '',
    to_date: '',
    from_time: '',
    to_time: '',
    shift_id: '',
    schedule_id: '',
    total_units: 0,
    unit_type: 'DAYS',
    reason: '',
    status: 'PENDING',
    approved_by: '',
    approved_at: '',
    remarks: ''
  });
  const [applications, setApplications] = useState([]);
  const [leaveSummary, setLeaveSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [shift, setShift] = useState(null);

  const [canWrite, setCanWrite] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [dataScope, setDataScope] = useState("All Data");
  const [statusPermissions, setStatusPermissions] = useState({ pending: false, approved: false, rejected: false });
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);

  const activeBranchLocalUI = (localStorage.getItem('active_branch') || 'All Branches').trim();
  const isSpecificBranchActiveUI = activeBranchLocalUI.toLowerCase() !== 'all branches' && activeBranchLocalUI.toLowerCase() !== 'all' && activeBranchLocalUI !== '';
  const showCompanyAssign = !isSpecificBranchActiveUI && (isGroupAdmin || isCompanyAdmin);
  const showBranchAssign = !isSpecificBranchActiveUI && (isGroupAdmin || isCompanyAdmin);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedAddBranches, setSelectedAddBranches] = useState([]);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const userObj = JSON.parse(userStr);
          const role = userObj.role || userObj.UserType || "";
          const roleNorm = role.toLowerCase().replace(/[\s_]/g, '');
          const isGA = checkIsGlobalAdmin(userObj);
          const isCA = roleNorm === 'companyadmin' || roleNorm === 'admin' || roleNorm === 'manager';
          const isAdmin = isGA || isCA;
          setIsCompanyAdmin(isAdmin);
          setIsGroupAdmin(isGA);
          const userLocal = userObj;
          const activeContext = localStorage.getItem('active_company');
          let comps = [];
          if (userLocal.companies && userLocal.companies.length > 0) {
            comps = userLocal.companies.map(c => typeof c === 'object' && c !== null ? (c.company_name || c.company || c.name || '') : c).filter(c => c && typeof c === 'string');
          } else if (userLocal.company_name || userLocal.company) {
            comps = [userLocal.company_name || userLocal.company];
          } else {
            const currentBaseUrl = baseUrl || '';
            const res = await axios.get(`${currentBaseUrl}/api/company-details`, { headers: getHeaders() });
            const details = res.data.companyDetails || [];
            comps = details.map(d => d.restaurantName || d.company_name).filter(n => n);
          }
          setAllCompanies([...new Set(comps)]);
          if (activeContext && activeContext !== 'All') {
            setSelectedCompanies([activeContext || userLocal.company_name || ""]);
          } else {
            setSelectedCompanies([]);
          }
          if (role) {
            const userEmail = userLocal.email || '';
            const url = `${baseUrl}/api/role-permissions?role=${role}&email=${userEmail}`;
            const response = await axios.get(url, { headers: getHeaders() });
            const perms = response.data.permissions || [];
            const pagePerm = perms.find(p => p.pageId === 'leave_apply');
            if (pagePerm) {
              setCanWrite(pagePerm.canWrite === true);
              setCanDelete(pagePerm.canDelete === true);
              setDataScope(pagePerm.dataAccess || pagePerm.dataScope || "All Data");
              const sPerms = {
                pending: pagePerm.showPending !== false,
                approved: pagePerm.showApproved !== false,
                rejected: pagePerm.showRejected !== false
              };
              setStatusPermissions(sPerms);
              setForm(prev => {
                if (prev.status === 'PENDING' && !sPerms.pending) {
                  if (sPerms.approved) return { ...prev, status: 'APPROVED' };
                  if (sPerms.rejected) return { ...prev, status: 'REJECTED' };
                }
                return prev;
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
    };
    const fetchBranches = async () => {
      try {
        const currentBaseUrl = baseUrl || '';
        const response = await axios.get(`${currentBaseUrl}/api/branches`, { headers: getHeaders() });
        const branchData = response.data.map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name));
        setAvailableBranches(branchData);
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };
    if (baseUrl !== undefined) {
      fetchPermissions();
      fetchBranches();
    }
  }, [baseUrl]);

  useEffect(() => {
    if (!configLoading && baseUrl !== undefined) {
      fetchData();
    }
  }, [configLoading, baseUrl, dataScope]);

  const fetchSummary = useCallback(async () => {
    if (!form.employee_id || configLoading) { setLeaveSummary([]); return; }
    try {
      const res = await axios.get(`${baseUrl}/api/leave-summary`, { params: { employee_id: form.employee_id }, headers: getHeaders() });
      setLeaveSummary(res.data || []);
    } catch (err) { console.error('Error fetching leave summary:', err); }
  }, [form.employee_id, baseUrl, configLoading, getHeaders]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  useEffect(() => {
    const fetchRoster = async () => {
      if (!form.employee_id || !form.from_date || configLoading) {
        setShift(null);
        setForm(prev => ({ ...prev, shift_id: '', schedule_id: '' }));
        if (form.leave_mode !== 'HOURLY') setForm(prev => ({ ...prev, from_time: '', to_time: '' }));
        return;
      }
      try {
        const res = await axios.get(`${baseUrl}/api/roster`, { params: { employee_id: form.employee_id, date: form.from_date }, headers: getHeaders() });
        const shiftData = res.data;
        if (shiftData && shiftData.shift_id) {
          setShift(shiftData);
          setForm(prev => ({ ...prev, shift_id: shiftData.shift_id, schedule_id: shiftData.schedule_id }));
          setError('');
        } else {
          setShift(null);
          setForm(prev => ({ ...prev, shift_id: '', schedule_id: '' }));
        }
      } catch (err) { console.error('Error fetching roster:', err); setShift(null); }
    };
    fetchRoster();
  }, [form.employee_id, form.from_date, baseUrl, configLoading, getHeaders]);

  useEffect(() => { calculateUnits(); }, [form.from_date, form.to_date, form.leave_mode, form.from_time, form.to_time, shift]);

  const calculateUnits = () => {
    let units = 0; let type = 'DAYS';
    if (form.leave_mode === 'HOURLY') {
      type = 'MINUTES';
      if (form.from_time && form.to_time) {
        const [h1, m1] = form.from_time.split(':').map(Number);
        const [h2, m2] = form.to_time.split(':').map(Number);
        const diff = (h2 * 60 + m2) - (h1 * 60 + m1);
        units = diff > 0 ? diff : 0;
      }
    } else if (form.leave_mode === 'HALF_DAY') {
      units = 0.5;
    } else {
      if (form.from_date && form.to_date) {
        const d1 = new Date(form.from_date); const d2 = new Date(form.to_date);
        const diffDays = Math.ceil(Math.abs(d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
        units = diffDays > 0 ? diffDays : 0;
      }
    }
    setForm(prev => ({ ...prev, total_units: units, unit_type: type }));
  };

  const fetchData = async () => {
    if (configLoading) return;
    try {
      setLoading(true);
      const headers = getHeaders();
      const [empRes, typesRes, appsRes] = await Promise.all([
        axios.get(`${baseUrl}/api/add-employee`, { params: { userId: user?.userId || user?._id || user?.id, role: user?.role, pageId: 'leave_apply' }, headers }),
        axios.get(`${baseUrl}/api/leave-types`, { headers }),
        axios.get(`${baseUrl}/api/leave-applications`, { params: { userId: user?.userId || user?._id || user?.id, role: user?.role }, headers })
      ]);
      const employeeList = empRes.data || [];
      setEmployees(employeeList);
      if (employeeList.length > 0 && !form.employee_id) {
        setForm(prev => ({ ...prev, employee_id: employeeList[0]._id || employeeList[0].id }));
      }
      setLeaveTypes((typesRes.data || []).filter(t => t.is_active));
      setApplications(appsRes.data || []);
      setError('');
    } catch (err) { console.error('Error fetching data:', err); }
    finally { setLoading(false); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canWrite) {
      setError("You do not have permission to submit leave requests.");
      setTimeout(() => setError(''), 3000);
      return;
    }
    processSubmit();
  };

  const processSubmit = async () => {
    const activeCtx = localStorage.getItem('active_company') || 'All';
    const userStr = localStorage.getItem('user');
    const userData = userStr ? JSON.parse(userStr) : {};
    const userComp = userData.company_name || userData.company || '';
    const companiesToProcess = showCompanyAssign
      ? selectedCompanies
      : (activeCtx === 'All' ? selectedCompanies : [activeCtx || userComp || ''].filter(Boolean));

    setLoading(true);
    setError('');
    setShowAssignModal(false);

    try {
      if (form.from_date > form.to_date) throw new Error("From Date cannot be later than To Date");
      const payload = { ...form, company_names: companiesToProcess, branch_names: selectedAddBranches, applied_at: new Date().toISOString() };
      const headers = getHeaders();
      if (editingId) {
        await axios.put(`${baseUrl}/api/leave-applications/${editingId}`, payload, { headers });
        setMessage('Leave application updated successfully!');
        await axios.post(`${baseUrl}/api/notifications`, {
          type: 'info', title: 'Leave Application Updated',
          message: `Leave application for ${employees.find(e => (e._id || e.id) === form.employee_id)?.name || 'Employee'} has been updated to ${form.status}.`,
          category: 'leave', userId: user?.userId || user?._id || user?.id, employeeId: form.employee_id,
          data: { leave_id: editingId, status: form.status }
        }, { headers });
        window.dispatchEvent(new Event('notificationUpdate'));
      } else {
        const resp = await axios.post(`${baseUrl}/api/leave-applications`, payload, { headers });
        setMessage('Leave request submitted successfully!');
        await axios.post(`${baseUrl}/api/notifications`, {
          type: 'success', title: 'New Leave Application',
          message: `${employees.find(e => (e._id || e.id) === form.employee_id)?.name || 'Employee'} has applied for ${form.leave_mode} leave.`,
          category: 'leave', userId: user?.userId || user?._id || user?.id, employeeId: form.employee_id,
          data: { leave_id: resp.data?.inserted_id || resp.data?._id, status: 'PENDING' }
        }, { headers });
        window.dispatchEvent(new Event('notificationUpdate'));
      }
      setTimeout(() => setMessage(''), 2000);
      setForm(prev => ({
        ...prev, leave_mode: 'FULL_DAY', from_date: '', to_date: '', from_time: '', to_time: '',
        shift_id: '', schedule_id: '', total_units: 0, unit_type: 'DAYS', reason: '',
        status: statusPermissions.pending ? 'PENDING' : (statusPermissions.approved ? 'APPROVED' : 'PENDING'),
        approved_by: '', approved_at: '', remarks: ''
      }));
      setEditingId(null);
      setShift(null);
      await fetchData();
      await fetchSummary();
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || 'Failed to submit leave request.';
      setError(errMsg);
      setTimeout(() => setError(''), 3000);
    } finally { setLoading(false); }
  };

  const handleEdit = (app) => {
    setForm({
      employee_id: app.employee_id || '', leave_type_id: app.leave_type_id || '',
      leave_mode: app.leave_mode || 'FULL_DAY', from_date: app.from_date || app.leave_date || '',
      to_date: app.to_date || app.leave_date || '', from_time: app.from_time || '',
      to_time: app.to_time || '', shift_id: app.shift_id || '', schedule_id: app.schedule_id || '',
      total_units: app.total_units || 0, unit_type: app.unit_type || 'DAYS',
      reason: app.reason || '', status: app.status || 'PENDING',
      approved_by: app.approved_by || '', approved_at: app.approved_at || '', remarks: app.remarks || ''
    });
    setEditingId(app.leave_id || app._id);
  };

  const handleDelete = (id) => { setDeletingId(id); setShowDeleteConfirm(true); };

  const confirmDelete = async () => {
    if (!canDelete) {
      setError("You do not have permission to delete leave requests.");
      setTimeout(() => setError(''), 3000);
      setShowDeleteConfirm(false);
      return;
    }
    try {
      setLoading(true);
      await axios.delete(`${baseUrl}/api/leave-applications/${deletingId}`, { headers: getHeaders() });
      setMessage('Leave application deleted successfully!');
      setTimeout(() => setMessage(''), 2000);
      setShowDeleteConfirm(false);
      await fetchData();
      await fetchSummary();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete leave application.');
      setTimeout(() => setError(''), 3000);
    } finally { setDeletingId(null); setShowDeleteConfirm(false); setLoading(false); }
  };

  const closeDeleteConfirm = (e) => {
    if (e && e.target !== e.currentTarget) return;
    setShowDeleteConfirm(false);
    setDeletingId(null);
  };

  const clearForm = () => {
    setForm({ employee_id: '', leave_type_id: '', leave_mode: 'FULL_DAY', from_date: '', to_date: '', from_time: '', to_time: '', shift_id: '', schedule_id: '', total_units: 0, unit_type: 'DAYS', reason: '', status: 'PENDING', approved_by: '', approved_at: '', remarks: '' });
    setEditingId(null); setShift(null); setMessage(''); setError('');
  };

  const getStatusClass = (status) => {
    if (status === 'APPROVED') return 'active';
    if (status === 'REJECTED') return 'inactive';
    return 'pending';
  };

  const getEmployeeName = (app) => {
    if (app.employee_name && app.employee_name !== 'N/A') return app.employee_name;
    const emp = employees.find(e => (e._id === app.employee_id || e.id === app.employee_id));
    return emp ? emp.name : 'Unknown';
  };

  const getLeaveName = (app) => {
    if (app.leave_name && app.leave_name !== 'N/A') return `${app.leave_name} (${app.leave_code || ''})`;
    const lt = leaveTypes.find(t => (t.leave_type_id === app.leave_type_id || t._id === app.leave_type_id));
    return lt ? `${lt.leave_name} (${lt.leave_code || ''})` : 'Unknown';
  };

  return (
    <div className="create-customer-container main-customer-page">
      {/* Header */}
      <div className="customer-header-section">
        <div className="header-left">
          <button onClick={() => navigate('/admin')} className="header-back-btn" disabled={loading}>
            <FaArrowLeft /> Back
          </button>
        </div>
        <div className="header-center">
          <h1>Leave Applications ({applications.length})</h1>
        </div>
        <div className="header-right">
          <div className="header-actions">
            {(isGroupAdmin || isCompanyAdmin) && (
              <button onClick={() => setShowCustomizeModal(true)} className="save-btn" style={{ backgroundColor: '#00d284', width: 'auto', padding: '0 20px' }} disabled={loading}>
                <FaCog /> Customize
              </button>
            )}
            <button onClick={() => navigate('/leave-type')} className="save-btn" style={{ backgroundColor: '#4B76E6', width: 'auto', padding: '0 20px' }} disabled={loading}>
              <FaCalendarAlt /> Manage Leave Types
            </button>
            <button type="submit" form="leaveApplyForm" disabled={loading || !form.employee_id || !form.leave_type_id || !form.from_date || !form.to_date} className="save-btn" style={{ backgroundColor: '#20c997', width: 'auto', padding: '0 20px' }}>
              {loading ? 'Submitting...' : editingId ? 'Update Application' : 'Submit Leave Request'}
            </button>
          </div>
        </div>
      </div>

      <div className="customer-scroll-body">
        <div className="form-sections-wrapper">

          {/* Error */}
          {error && (
            <div style={{ background: '#fef2f2', color: '#ef4444', padding: '16px 20px', borderRadius: '14px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px', fontWeight: '600', border: '1px solid #fee2e2' }}>
              <FaExclamationTriangle style={{ fontSize: '20px' }} /> {error}
            </div>
          )}

          {/* Success Popup */}
          {message && (
            <div onClick={() => setMessage('')} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, cursor: 'pointer' }}>
              <div onClick={e => e.stopPropagation()} style={{ background: '#fff', padding: '40px 32px', borderRadius: '24px', width: '90%', maxWidth: '440px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', cursor: 'default' }}>
                <div style={{ width: '84px', height: '84px', borderRadius: '50%', background: '#ecfdf5', color: '#10b981', border: '2px solid #a7f3d0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '38px', marginBottom: '24px' }}>
                  <FaCheckCircle />
                </div>
                <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: '0 0 12px 0' }}>Success</h3>
                <p style={{ fontSize: '16px', color: '#64748b', margin: 0, fontWeight: '500' }}>{message}</p>
              </div>
            </div>
          )}

          {/* Leave Balance Summary */}
          {form.employee_id && leaveSummary.length > 0 && (
            <div className="form-section-card theme-blue" style={{ marginBottom: '24px' }}>
              <div className="section-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>
                <div className="section-icon" style={{ background: '#e0e7ff', color: '#4f46e5', width: '40px', height: '40px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                  <FaInfoCircle />
                </div>
                <h2 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>Leave Balance Summary</h2>
              </div>
              <div className="premium-table-wrapper">
                <table className="premium-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Leave Type</th>
                      <th style={{ textAlign: 'center' }}>Total Allocated</th>
                      <th style={{ textAlign: 'center' }}>Monthly Credit</th>
                      <th style={{ textAlign: 'center' }}>Pending</th>
                      <th style={{ textAlign: 'center' }}>Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveSummary.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ fontWeight: '600' }}>{item.leave_name || '-'}</td>
                        <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#10b981' }}>
                          {item.total_allocated != null ? item.total_allocated : 0}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#64748b' }}>
                          {item.monthly_credit != null ? item.monthly_credit : 0}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#f59e0b' }}>
                          {item.pending != null ? item.pending : 0}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#3b82f6' }}>
                          {item.available != null ? item.available : 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Form */}
          <form id="leaveApplyForm" onSubmit={handleSubmit}>
            <div className="form-section-card theme-blue" style={{ marginBottom: '24px' }}>
              <div className="section-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
                <div className="section-icon" style={{ background: '#e0e7ff', color: '#4f46e5', width: '40px', height: '40px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                  <FaCalendarAlt />
                </div>
                <h2 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>{editingId ? 'Edit Leave Application' : 'Submit Leave Request'}</h2>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                {isFieldVisible('employee_id') && (
                  <div className="form-field-group">
                    <label className="field-label">Employee</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '0 12px', borderRadius: '10px', border: '2px solid #e2e8f0', height: '44px' }}>
                      <FaSearch style={{ color: '#94a3b8', flexShrink: 0 }} />
                      <select required value={form.employee_id} onChange={e => setForm({ ...form, employee_id: e.target.value })} style={{ flex: 1, border: 'none', background: 'transparent', fontSize: '14px', color: '#1e293b', outline: 'none', width: '100%' }}>
                        <option value="">-- Select Employee --</option>
                        {employees.map(emp => (
                          <option key={emp._id || emp.id} value={emp._id || emp.id}>{emp.name} ({emp.employeeId})</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                {isFieldVisible('leave_type_id') && (
                  <div className="form-field-group">
                    <label className="field-label">Leave Type</label>
                    <select className="field-input" required value={form.leave_type_id} onChange={e => setForm({ ...form, leave_type_id: e.target.value })}>
                      <option value="">-- Select Leave Type --</option>
                      {leaveTypes.map(lt => (
                        <option key={lt.leave_type_id || lt._id} value={lt.leave_type_id || lt._id}>{lt.leave_name} ({lt.leave_code})</option>
                      ))}
                    </select>
                  </div>
                )}
                {isFieldVisible('leave_mode') && (
                  <div className="form-field-group">
                    <label className="field-label">Leave Mode</label>
                    <select className="field-input" value={form.leave_mode} onChange={e => setForm({ ...form, leave_mode: e.target.value })}>
                      <option value="FULL_DAY">Full Day</option>
                      <option value="HALF_DAY">Half Day</option>
                      <option value="HOURLY">Hourly</option>
                    </select>
                  </div>
                )}
                {isFieldVisible('from_date') && (
                  <div className="form-field-group">
                    <label className="field-label">From Date</label>
                    <input type="date" className="field-input" required value={form.from_date} onChange={e => setForm({ ...form, from_date: e.target.value })} onClick={e => { try { e.target.showPicker(); } catch {} }} />
                  </div>
                )}
                {isFieldVisible('to_date') && (
                  <div className="form-field-group">
                    <label className="field-label">To Date</label>
                    <input type="date" className="field-input" required value={form.to_date} min={form.from_date} onChange={e => setForm({ ...form, to_date: e.target.value })} onClick={e => { try { e.target.showPicker(); } catch {} }} />
                  </div>
                )}
                {form.leave_mode === 'HOURLY' && (
                  <>
                    {isFieldVisible('from_time') && (
                      <div className="form-field-group">
                        <label className="field-label">From Time</label>
                        <input type="time" className="field-input" value={form.from_time} onChange={e => setForm({ ...form, from_time: e.target.value })} />
                      </div>
                    )}
                    {isFieldVisible('to_time') && (
                      <div className="form-field-group">
                        <label className="field-label">To Time</label>
                        <input type="time" className="field-input" value={form.to_time} onChange={e => setForm({ ...form, to_time: e.target.value })} />
                      </div>
                    )}
                  </>
                )}
                {isFieldVisible('status') && (
                  <div className="form-field-group">
                    <label className="field-label">Status</label>
                    <select className="field-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                      {statusPermissions.pending && <option value="PENDING">Pending</option>}
                      {statusPermissions.approved && <option value="APPROVED">Approved</option>}
                      {statusPermissions.rejected && <option value="REJECTED">Rejected</option>}
                      {!statusPermissions.pending && !statusPermissions.approved && !statusPermissions.rejected && <option value="">No Status Allowed</option>}
                    </select>
                  </div>
                )}
              </div>

              {isFieldVisible('reason') && (
                <div className="form-field-group" style={{ marginTop: '20px' }}>
                  <label className="field-label">Reason (Optional)</label>
                  <textarea className="field-input" placeholder="Enter reason for leave..." value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} rows="3" style={{ resize: 'vertical', height: 'auto' }} />
                </div>
              )}
              {isFieldVisible('remarks') && (
                <div className="form-field-group" style={{ marginTop: '16px' }}>
                  <label className="field-label">Remarks (Manager Only)</label>
                  <textarea className="field-input" placeholder="Admin remarks..." value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} rows="2" style={{ resize: 'vertical', height: 'auto' }} />
                </div>
              )}

              <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: '700', color: '#604be8', fontSize: '15px' }}>
                  Total: {form.total_units} {form.unit_type} (Auto-calculated)
                </span>
                {(() => {
                  const currentSummary = leaveSummary.find(s => s.leave_type_id === form.leave_type_id);
                  if (currentSummary && form.total_units > currentSummary.available) {
                    return (
                      <div style={{ padding: '8px 14px', borderRadius: '10px', background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600' }}>
                        <FaExclamationTriangle />
                        <span><strong>Warning:</strong> Exceeding available balance ({currentSummary.available}). May be considered LOP.</span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {editingId && (
                <div style={{ marginTop: '16px' }}>
                  <button type="button" onClick={clearForm} className="cancel-btn">Cancel Edit</button>
                </div>
              )}
            </div>
          </form>

          {/* Applications Table */}
          <div style={{ marginBottom: '16px' }}>
            <h2 style={{ fontSize: '18px', color: '#1e293b', margin: 0, fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaUserTie style={{ color: '#604be8' }} /> Leave Applications
            </h2>
          </div>

          {applications.length > 0 ? (
            <div className="premium-table-wrapper">
              <table className="premium-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    {isFieldVisible('employee_id') && <th>Employee</th>}
                    {isFieldVisible('leave_type_id') && <th>Type</th>}
                    {isFieldVisible('from_date') && <th style={{ textAlign: 'center' }}>Start Date</th>}
                    {isFieldVisible('to_date') && <th style={{ textAlign: 'center' }}>End Date</th>}
                    {isFieldVisible('leave_mode') && <th style={{ textAlign: 'center' }}>Mode</th>}
                    {(isFieldVisible('from_time') || isFieldVisible('to_time')) && <th style={{ textAlign: 'center' }}>Time</th>}
                    {isFieldVisible('total_units') && <th style={{ textAlign: 'center' }}>Units</th>}
                    {isFieldVisible('status') && <th style={{ textAlign: 'center' }}>Status</th>}
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.map(app => (
                    <tr key={app.leave_id || app._id}>
                      {isFieldVisible('employee_id') && <td style={{ fontWeight: '600' }}>{getEmployeeName(app)}</td>}
                      {isFieldVisible('leave_type_id') && <td>{getLeaveName(app)}</td>}
                      {isFieldVisible('from_date') && <td style={{ textAlign: 'center' }}>{app.from_date || app.leave_date}</td>}
                      {isFieldVisible('to_date') && <td style={{ textAlign: 'center' }}>{app.to_date || app.leave_date}</td>}
                      {isFieldVisible('leave_mode') && <td style={{ textAlign: 'center' }}>{app.leave_mode || 'FULL_DAY'}</td>}
                      {(isFieldVisible('from_time') || isFieldVisible('to_time')) && (
                        <td style={{ textAlign: 'center' }}>{app.leave_mode === 'HOURLY' ? `${app.from_time || ''} - ${app.to_time || ''}` : '-'}</td>
                      )}
                      {isFieldVisible('total_units') && <td style={{ textAlign: 'center' }}>{app.total_units} {app.unit_type}</td>}
                      {isFieldVisible('status') && (
                        <td style={{ textAlign: 'center' }}>
                          <span style={{
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: '700',
                            backgroundColor: app.status === 'APPROVED' ? '#dcfce7' : app.status === 'REJECTED' ? '#fee2e2' : '#fef9c3',
                            color: app.status === 'APPROVED' ? '#166534' : app.status === 'REJECTED' ? '#991b1b' : '#854d0e',
                            display: 'inline-block',
                            textTransform: 'uppercase',
                            border: `1px solid ${app.status === 'APPROVED' ? '#bbf7d0' : app.status === 'REJECTED' ? '#fecaca' : '#fef08a'}`
                          }}>
                            {app.status || 'PENDING'}
                          </span>
                        </td>
                      )}
                      <td>
                        <div className="action-buttons">
                          <button onClick={() => {
                            if (!canWrite) { setError("No permission to edit."); setTimeout(() => setError(''), 3000); return; }
                            handleEdit(app);
                          }} className="action-btn edit" title="Edit"><FaEdit /></button>
                          <button onClick={() => {
                            if (!canDelete) { setError("No permission to delete."); setTimeout(() => setError(''), 3000); return; }
                            handleDelete(app.leave_id || app._id);
                          }} className="action-btn delete" title="Delete" disabled={loading}><FaTrash /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ marginTop: '10px', paddingLeft: '10px' }}>
              <h2 style={{ fontSize: '18px', color: '#1e293b', margin: 0, fontWeight: '600' }}>Application List</h2>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="add-modal-overlay" onClick={closeDeleteConfirm}>
          <div className="add-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
              <span><FaTrash style={{ fontSize: '18px' }} /> Confirm Delete</span>
              <button className="close-modal-btn" onClick={closeDeleteConfirm}><FaTimes /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this leave application? This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={closeDeleteConfirm} style={{ background: '#f1f5f9', color: '#64748b' }}>Cancel</button>
              <button className="danger-btn" onClick={confirmDelete} disabled={loading}>{loading ? 'Deleting...' : 'Yes, Delete'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Companies & Branches Modal */}
      {showAssignModal && (
        <div className="add-modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="add-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <span><FaBuilding style={{ fontSize: '20px' }} /> Assign Companies & Branches</span>
              <button className="close-modal-btn" onClick={() => setShowAssignModal(false)}><FaTimes /></button>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {showCompanyAssign && (
                <div style={{ marginBottom: '24px' }}>
                  <p>Select Companies</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                    {allCompanies.map((comp, idx) => {
                      const isSelected = selectedCompanies.includes(comp);
                      return (
                        <div key={idx} style={{ border: isSelected ? '2px solid #604be8' : '2px solid #e2e8f0', padding: '12px 16px', borderRadius: '14px', backgroundColor: isSelected ? '#f5f3ff' : '#f8fafc', cursor: 'pointer', transition: 'all 0.2s' }} onClick={() => {
                          if (isSelected) setSelectedCompanies(prev => prev.filter(c => c !== comp));
                          else setSelectedCompanies(prev => [...prev, comp]);
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input type="checkbox" checked={isSelected} readOnly style={{ width: '18px', height: '18px', accentColor: '#604be8', cursor: 'pointer' }} />
                            <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>{comp}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {showBranchAssign && selectedCompanies.length > 0 && availableBranches.length > 0 && (
                <div>
                  <p>Select Branches</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                    {availableBranches.map((branch, idx) => {
                      const isSelected = selectedAddBranches.includes(branch);
                      return (
                        <div key={idx} style={{ border: isSelected ? '2px solid #604be8' : '2px solid #e2e8f0', padding: '12px 16px', borderRadius: '14px', backgroundColor: isSelected ? '#f5f3ff' : '#f8fafc', cursor: 'pointer', transition: 'all 0.2s' }} onClick={() => {
                          if (isSelected) setSelectedAddBranches(prev => prev.filter(b => b !== branch));
                          else setSelectedAddBranches(prev => [...prev, branch]);
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input type="checkbox" checked={isSelected} readOnly style={{ width: '18px', height: '18px', accentColor: '#604be8', cursor: 'pointer' }} />
                            <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>{branch}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowAssignModal(false)} style={{ background: '#f1f5f9', color: '#64748b' }}>Cancel</button>
              <button className="save-btn" onClick={processSubmit} disabled={loading} style={{ background: '#604be8' }}>{loading ? 'Submitting...' : 'Confirm & Submit'}</button>
            </div>
          </div>
        </div>
      )}

      <CustomerCustomizationModal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        onRefresh={handleCustomizationRefresh}
        targetDocType="Leave Apply"
      />
    </div>
  );
};

export default LeaveApply;
